import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { CaretLeft, CalendarBlank } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PRICING } from "@/data/categories";
import { RefundAgree } from "@/components/RefundAgree";
import { PayButtons } from "@/components/PayButtons";

const PRODUCT = PRICING.ai.find((a) => a.key === "yearly_reflection");
const BACK = "/timeline?view=keepsakes";

export default function YearlyCheckout() {
  const year = Number(useParams().year);
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [refundOk, setRefundOk] = useState(false);

  useEffect(() => {
    api.get("/keepsakes/meta").then((r) => {
      const y = r.data.years.find((x) => x.year === year);
      const need = r.data.thresholds.yearly_reflection;
      if ((r.data.yearly_years || []).includes(year)) { toast("You already own this year's reflection."); navigate(BACK, { replace: true }); return; }
      if (!y || y.count < need) { toast.error(`Add at least ${need} memories in ${year} first.`); navigate(BACK, { replace: true }); return; }
      setInfo({ count: y.count });
    });
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info || !cfg) return <p className="p-10 text-center text-[#8A7F76]">Loading…</p>;

  const onPaid = (r) => {
    if (r.payment_status === "paid") toast.success(`Your ${year} Yearly Reflection is unlocked.`);
    else toast(`You already owned ${year}. This duplicate payment is being refunded.`);
    navigate(`${BACK}&year=${year}`);
  };

  const body = (
    <div data-testid="yearly-checkout" className="mx-auto max-w-lg px-6 py-10">
      <button data-testid="yearly-checkout-back" onClick={() => navigate(BACK)} className="mb-8 flex items-center gap-1 text-sm text-[#8A7F76] hover:text-[#A84A32]">
        <CaretLeft size={16} /> Back to Keepsakes
      </button>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#A84A32]"><CalendarBlank size={14} weight="fill" /> {PRODUCT.label}</p>
      <h1 data-testid="yearly-checkout-year" className="mt-2 font-serif text-5xl text-[#2C2A28]">{year}</h1>
      <p className="mt-1 text-sm text-[#8A7F76]">{info.count} memories saved in {year}</p>
      <p className="mt-5 leading-relaxed text-[#4A4540]">{PRODUCT.desc}</p>
      <div className="mt-6 rounded-xl border border-[#B08D57]/30 bg-white p-5">
        <p className="font-serif text-4xl text-[#2C2A28]" data-testid="yearly-checkout-price">{PRICING.currency}{PRODUCT.price.toFixed(2)}</p>
        <p className="text-xs text-[#8A7F76]">AUD · No GST applies · one-time · for {year} only</p>
        <div className="my-4"><RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-yearly" /></div>
        <PayButtons payload={{ lookup_key: "yearly_reflection", year }} refundOk={refundOk} paypalReady={!!cfg?.client_id} paypalMode={cfg?.mode} cardEnabled={cfg?.card_enabled !== false}
          onPaid={onPaid} cardLabel={`Pay with Card · ${year}`} testPrefix="yearly" />
        <p className="mt-4 text-xs leading-relaxed text-[#8A7F76]">
          This purchase covers {year} only. Other years are bought separately. After payment, give AI consent and create your
          reflection — you can reread and download it anytime, and retry free if writing fails.
        </p>
      </div>
    </div>
  );

  return cfg?.client_id ? (
    <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "AUD", intent: "capture", components: "buttons", disableFunding: "card" }}>
      {body}
    </PayPalScriptProvider>
  ) : body;
}
